/**
 * Layout gates (03 §5.2): nothing overlaps, nothing solid stands in water or in a ravine,
 * every full-width barrier has two crossings well apart, every approach band has its
 * depression and its open ground for pods.
 */

import { type Collider, collidersFor, PROPS } from '../../sim/props';
import { inPond, inTrench } from '../../sim/statics';
import type { MissionDef } from '../../sim/types';
import { LINES, VALIDATE } from '../tuning';
import type { Band } from '../types';
import { inBarrier, type NavProbe } from './nav';

/** How far two footprints interpenetrate (metres); 0 or less when they are clear of each other. */
function penetration(a: Collider, b: Collider): number {
  if (a.shape === 'circle' && b.shape === 'circle') {
    return a.r + b.r - Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));
  }
  if (a.shape === 'box' && b.shape === 'box') {
    return Math.min(a.hx + b.hx - Math.abs(a.x - b.x), a.hz + b.hz - Math.abs(a.z - b.z));
  }
  const circle = a.shape === 'circle' ? a : b;
  const box = a.shape === 'circle' ? b : a;
  const nx = Math.min(box.x + box.hx, Math.max(box.x - box.hx, circle.x));
  const nz = Math.min(box.z + box.hz, Math.max(box.z - box.hz, circle.z));
  return circle.r - Math.sqrt((circle.x - nx) * (circle.x - nx) + (circle.z - nz) * (circle.z - nz));
}

export function checkProps(def: MissionDef): string[] {
  const out: string[] = [];
  const solid: Collider[] = [];
  const kindOf = new Map<number, string>();
  const ids = new Set<number>();
  for (const p of def.props) {
    if (ids.has(p.id)) out.push(`prop id ${p.id} is used twice`);
    ids.add(p.id);
    kindOf.set(p.id, p.kind);
    if (p.x < 0 || p.z < 0 || p.x > def.sizeX || p.z > def.sizeZ) out.push(`${p.kind} ${p.id} is off the map`);
    const colliders = collidersFor(p).filter((c) => c.blocksMove);
    solid.push(...colliders);
    if (colliders.length === 0) continue;
    // A bridge pier or a moored barge belongs in the water (`PropSpec.wet`, 10 §3); nothing else does.
    const wet = PROPS[p.kind].wet === true;
    if (!wet && (inPond(def, p.x, p.z) || inBarrier(def, p.x, p.z))) out.push(`${p.kind} ${p.id} stands in water`);
    // Runs may end beside a ravine; nothing may stand in one.
    if (inTrench(def, p.x, p.z) && p.length === undefined) out.push(`${p.kind} ${p.id} stands in a depression`);
  }
  // Sweep along x: only footprints whose x-extents meet can overlap.
  const reach = (c: Collider): number => (c.shape === 'circle' ? c.r : c.hx);
  solid.sort((a, b) => a.x - reach(a) - (b.x - reach(b)));
  for (let i = 0; i < solid.length; i++) {
    const a = solid[i];
    if (!a) continue;
    for (let j = i + 1; j < solid.length; j++) {
      const b = solid[j];
      if (!b || b.x - reach(b) > a.x + reach(a)) break;
      if (a.propId === b.propId) continue;
      if (penetration(a, b) > VALIDATE.overlapTolerance) {
        out.push(`${kindOf.get(a.propId)} ${a.propId} overlaps ${kindOf.get(b.propId)} ${b.propId}`);
        if (out.length > 12) return out;
      }
    }
  }
  return out;
}

/** Rows that are almost wholly blocked are linear obstacles: each needs two crossings well apart (03 §5.1 step 3). */
export function checkBarriers(def: MissionDef, nav: NavProbe): string[] {
  const out: string[] = [];
  const { width } = nav;
  let reported = -10;
  for (let z = Math.ceil(def.goalZone.z1) + 1; z < Math.floor(def.dropBand.z0) + 2 && z < nav.height; z++) {
    let shut = 0;
    const runs: number[] = [];
    let runStart = -1;
    for (let x = 0; x <= width; x++) {
      const open = x < width && nav.hard[z * width + x] === 0 && nav.soft[z * width + x] === 0;
      if (!open && x < width) shut++;
      if (open && runStart < 0) runStart = x;
      if (!open && runStart >= 0) {
        if (x - runStart >= 2) runs.push((runStart + x) / 2);
        runStart = -1;
      }
    }
    if (shut < width * VALIDATE.barrierRow || z - reported < 4) continue;
    const spread = runs.length > 0 ? Math.max(...runs) - Math.min(...runs) : 0;
    if (runs.length < 2 || spread < LINES.minCrossingSpacing) {
      const need = LINES.minCrossingSpacing;
      out.push(`the barrier at z = ${z} needs two crossings at least ${need} m apart (has ${runs.length})`);
      reported = z;
    }
  }
  return out;
}

/** Disjoint open squares in a band: no props, no depression, no water, no mines (02 §3.2). */
function podZones(nav: NavProbe, band: Band): number {
  const size = VALIDATE.podZone;
  const used = new Uint8Array(nav.width * nav.height);
  let found = 0;
  for (let z = Math.ceil(band.z0); z + size <= Math.floor(band.z1); z += 2) {
    for (let x = 2; x + size <= nav.width - 2; x += 2) {
      let open = true;
      for (let dz = 0; dz < size && open; dz++) {
        for (let dx = 0; dx < size && open; dx++) {
          const i = (z + dz) * nav.width + x + dx;
          open = nav.full.blocked[i] === 0 && nav.soft[i] === 0 && used[i] === 0 && nav.costly[i] === 0;
        }
      }
      if (!open) continue;
      for (let dz = 0; dz < size; dz++) used.fill(1, (z + dz) * nav.width + x, (z + dz) * nav.width + x + size);
      found++;
      if (found >= VALIDATE.podZonesPerBand) return found;
    }
  }
  return found;
}

/** Does a north-south-ish depression run through at least half the band's depth? */
function hasRavine(def: MissionDef, band: Band): boolean {
  let covered = 0;
  for (const t of def.trenches) {
    for (let i = 1; i < t.points.length; i++) {
      const a = t.points[i - 1];
      const b = t.points[i];
      if (!a || !b || Math.abs(b[1] - a[1]) < Math.abs(b[0] - a[0])) continue;
      covered += Math.max(0, Math.min(band.z1, Math.max(a[1], b[1])) - Math.max(band.z0, Math.min(a[1], b[1])));
    }
  }
  return covered >= (band.z1 - band.z0) * 0.5;
}

export function checkBands(def: MissionDef, nav: NavProbe, bands: readonly Band[]): string[] {
  const out: string[] = [];
  bands.forEach((band, i) => {
    if (band.kind !== 'approach') return;
    const zones = podZones(nav, band);
    if (zones < VALIDATE.podZonesPerBand) out.push(`approach band ${i} has only ${zones} open pod zones`);
    if (!hasRavine(def, band)) out.push(`approach band ${i} has no north-south depression`);
  });
  return out;
}
